import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  Timestamp,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiUrl, cloudRunApiUrlDev, mainApiUrl, apiUrlDevFirebase, cloudRunApiUrl } from "../../globalVariables";
import IncomeMismatchEvalPage from "../IncomeMismatchEval/page";
import "./DevTools.css";

// ─── Constants ───────────────────────────────────────────────────────────────

const DEVS = [
  { id: "joao",     label: "Joao",     color: "#7F7FD5" },
  { id: "jay",      label: "Jay",      color: "#E786D7" },
  { id: "shashank", label: "Shashank", color: "#86B7E7" },
  { id: "bhaulik",  label: "Bhaulik",  color: "#E7A686" },
  { id: "pedro",    label: "Pedro",    color: "#86E7A6" },
];

const STATUS_FLOW = ["open", "acknowledged", "done"];

const STATUS_LABELS = {
  open:         { label: "Open",         btnClass: "btn-danger" },
  acknowledged: { label: "Acknowledged", btnClass: "btn-primary" },
  done:         { label: "Done",         btnClass: "btn-success" },
};

const TIME_PERIODS = [
  { value: "PERIOD_1_HOUR",  label: "1h" },
  { value: "PERIOD_6_HOURS", label: "6h" },
  { value: "PERIOD_1_DAY",   label: "1d" },
  { value: "PERIOD_1_WEEK",  label: "7d" },
  { value: "PERIOD_30_DAYS", label: "30d" },
];

const FIRESTORE_COLLECTION = "adminErrorTracking";
const AUTO_REFRESH_MS = 60_000;

const ON_CALL_HANDBOOK_URL =
  "https://docs.google.com/document/d/1qTfLyffGOfIbc258AEHXsYvSYRN5NmIpaSFIscMdGy0/edit?usp=sharing";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCountClass(count) {
  if (count >= 100) return "devtools-count-critical";
  if (count >= 20)  return "devtools-count-high";
  if (count >= 5)   return "devtools-count-medium";
  return "devtools-count-low";
}

function devColor(devId) {
  return DEVS.find((d) => d.id === devId)?.color ?? "#64748b";
}

function devInitials(devId) {
  const dev = DEVS.find((d) => d.id === devId);
  return dev ? dev.label.slice(0, 2).toUpperCase() : "—";
}

function generateCursorPrompt({ errorMessage, stackTrace, service, firstSeen, lastSeen, count, consoleUrl }) {
  const stack = stackTrace
    ? stackTrace.split("\n").slice(0, 20).map((l) => l.trim()).join("\n")
    : "No stack trace available.";
  return `Fix the following production error in the Addy AI backend (project: hey-addy-chatgpt).

## Error Summary
- **Message**: ${errorMessage}
- **Service**: ${service || "unknown"}
- **Occurrences**: ${count}
- **First seen**: ${firstSeen}
- **Last seen**: ${lastSeen}
- **GCP Console**: ${consoleUrl}

## Stack Trace
\`\`\`
${stack}
\`\`\`

## Instructions
1. Identify the root cause from the stack trace above.
2. Locate the relevant file(s) in the codebase.
3. Propose a minimal, targeted fix — do NOT refactor outside the error scope.
4. Add appropriate error handling / logging if missing.
5. If the error is a known transient issue (e.g. network timeout), add retry logic.
6. Confirm the fix does not break existing tests.`.trim();
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DevToolsPage() {
  const [activeTab, setActiveTab] = useState("error-reporting");

  return (
    <div className="container-fluid p-4 DevToolsPageContainer">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <h1 className="mb-1">Dev Tools</h1>
      <p className="text-muted mb-4">
        Internal developer utilities and monitoring
      </p>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "error-reporting" ? "active" : ""}`}
            onClick={() => setActiveTab("error-reporting")}
          >
            Error reporting
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "copy-loan" ? "active" : ""}`}
            onClick={() => setActiveTab("copy-loan")}
          >
            Copy loan: Prod → Dev
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "copy-org" ? "active" : ""}`}
            onClick={() => setActiveTab("copy-org")}
          >
            Copy org: Prod → Dev
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "stale-tasks" ? "active" : ""}`}
            onClick={() => setActiveTab("stale-tasks")}
          >
            Stale Tasks
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "failed-documents" ? "active" : ""}`}
            onClick={() => setActiveTab("failed-documents")}
          >
            Failed documents
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "income-mismatch-eval" ? "active" : ""}`}
            onClick={() => setActiveTab("income-mismatch-eval")}
          >
            Income Eval
          </button>
        </li>
      </ul>

      {activeTab === "error-reporting" && <ErrorReportingTab />}
      {activeTab === "copy-loan" && <CopyLoanTab />}
      {activeTab === "copy-org" && <CopyOrgTab />}
      {activeTab === "stale-tasks" && <StaleTasksTab />}
      {activeTab === "failed-documents" && <FailedDocumentsTab />}
      {activeTab === "income-mismatch-eval" && <IncomeMismatchEvalPage />}
    </div>
  );
}

// ─── Error Reporting Tab ──────────────────────────────────────────────────────

function ErrorReportingTab() {
  const [view, setView]               = useState("live"); // "live" | "weekly"
  const [errors, setErrors]           = useState([]);
  const [metadata, setMetadata]       = useState({});  // keyed by groupId: { assignee, status, notes }
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [fetchErrorHelpUrl, setFetchErrorHelpUrl] = useState(null);
  const [timePeriod, setTimePeriod]   = useState("PERIOD_1_DAY");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [searchText, setSearchText]         = useState("");
  const [expandedRows, setExpandedRows]     = useState(new Set());
  const [copiedPrompts, setCopiedPrompts]   = useState(new Set());
  const [savedNotes, setSavedNotes]         = useState(new Set());
  const [savedPrLinks, setSavedPrLinks]     = useState(new Set());
  const [sortField, setSortField]           = useState("lastSeen");
  const [sortDir, setSortDir]               = useState("desc");
  const [lastRefreshed, setLastRefreshed]   = useState(null);
  const [countdown, setCountdown]           = useState(AUTO_REFRESH_MS / 1000);
  const [quickFilter, setQuickFilter]       = useState(null); // "open" | "latest" | null

  const autoRefreshTimer = useRef(null);
  const countdownTimer   = useRef(null);

  // ── Firestore metadata ──────────────────────────────────────────────────────

  const loadMetadata = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(collection(firestoreProd, FIRESTORE_COLLECTION), orderBy("updatedAt", "desc"))
      );
      const map = {};
      snap.forEach((d) => { map[d.id] = d.data(); });
      setMetadata(map);
    } catch (err) {
      console.error("Failed to load error metadata:", err);
    }
  }, []);

  // ── GCP fetch via backend proxy ─────────────────────────────────────────────

  const fetchErrors = useCallback(async (period = timePeriod, silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setFetchError(null);
    setFetchErrorHelpUrl(null);

    try {
      const res = await fetch(
        `${apiUrl}/gcp-errors?timePeriod=${period}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setFetchErrorHelpUrl(data.helpUrl || null);
        throw new Error(data.reason || `HTTP ${res.status}`);
      }

      setErrors(data.errors || []);
      setLastRefreshed(new Date());
    } catch (err) {
      setFetchError(err.message);
      if (!silent) toast.error(`Failed to fetch errors: ${err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timePeriod]);

  // ── Initial load ────────────────────────────────────────────────────────────

  useEffect(() => {
    loadMetadata();
    fetchErrors(timePeriod, false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-refresh every 60s ──────────────────────────────────────────────────

  useEffect(() => {
    const startAutoRefresh = () => {
      setCountdown(AUTO_REFRESH_MS / 1000);

      autoRefreshTimer.current = setTimeout(() => {
        fetchErrors(timePeriod, true);
        loadMetadata();
        startAutoRefresh();
      }, AUTO_REFRESH_MS);

      countdownTimer.current = setInterval(() => {
        setCountdown((c) => (c > 0 ? c - 1 : 0));
      }, 1000);
    };

    startAutoRefresh();

    return () => {
      clearTimeout(autoRefreshTimer.current);
      clearInterval(countdownTimer.current);
    };
  }, [timePeriod, fetchErrors, loadMetadata]);

  // ── Period change ───────────────────────────────────────────────────────────

  const handlePeriodChange = (period) => {
    setTimePeriod(period);
    clearTimeout(autoRefreshTimer.current);
    clearInterval(countdownTimer.current);
    fetchErrors(period, false);
  };

  // ── Manual refresh ──────────────────────────────────────────────────────────

  const handleManualRefresh = () => {
    clearTimeout(autoRefreshTimer.current);
    clearInterval(countdownTimer.current);
    fetchErrors(timePeriod, false);
    loadMetadata();
    setCountdown(AUTO_REFRESH_MS / 1000);
  };

  // ── Assignee change ─────────────────────────────────────────────────────────

  const handleAssigneeChange = async (groupId, assignee) => {
    const ref = doc(firestoreProd, FIRESTORE_COLLECTION, groupId);
    const current = metadata[groupId] || {};
    const updated = { ...current, assignee, updatedAt: serverTimestamp() };

    setMetadata((prev) => ({ ...prev, [groupId]: { ...current, assignee } }));

    try {
      await setDoc(ref, updated, { merge: true });
      toast.success(`Assigned to ${assignee}`);
    } catch (err) {
      toast.error("Failed to save assignee");
      setMetadata((prev) => ({ ...prev, [groupId]: current }));
    }
  };

  // ── Status change ───────────────────────────────────────────────────────────

  const handleStatusCycle = async (groupId, currentStatus) => {
    const currentIdx = STATUS_FLOW.indexOf(currentStatus || "open");
    const nextStatus = STATUS_FLOW[(currentIdx + 1) % STATUS_FLOW.length];

    const ref = doc(firestoreProd, FIRESTORE_COLLECTION, groupId);
    const current = metadata[groupId] || {};
    const updated = { ...current, status: nextStatus, updatedAt: serverTimestamp() };

    setMetadata((prev) => ({ ...prev, [groupId]: { ...current, status: nextStatus } }));

    try {
      await setDoc(ref, updated, { merge: true });

      // Also update GCP resolution status when moving to acknowledged/done
      if (nextStatus === "acknowledged" || nextStatus === "done") {
        const gcpStatus = nextStatus === "done" ? "RESOLVED" : "ACKNOWLEDGED";
        await fetch(`${apiUrl}/admin/gcp-errors/${groupId}/status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resolutionStatus: gcpStatus }),
        }).catch(() => {}); // Non-blocking — GCP update is best-effort
      }

      toast.success(`Status → ${nextStatus}`);
    } catch (err) {
      toast.error("Failed to save status");
      setMetadata((prev) => ({ ...prev, [groupId]: current }));
    }
  };

  // ── Notes save ──────────────────────────────────────────────────────────────

  const handleSaveNotes = async (groupId, notes) => {
    const ref = doc(firestoreProd, FIRESTORE_COLLECTION, groupId);
    const current = metadata[groupId] || {};

    try {
      await setDoc(ref, { ...current, notes, updatedAt: serverTimestamp() }, { merge: true });
      setMetadata((prev) => ({ ...prev, [groupId]: { ...current, notes } }));
      setSavedNotes((prev) => new Set([...prev, groupId]));
      setTimeout(() => setSavedNotes((prev) => { const s = new Set(prev); s.delete(groupId); return s; }), 2000);
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  // ── PR link save ────────────────────────────────────────────────────────────

  const handleSavePrLink = async (groupId, prLink) => {
    const ref = doc(firestoreProd, FIRESTORE_COLLECTION, groupId);
    const current = metadata[groupId] || {};

    try {
      await setDoc(ref, { ...current, prLink, updatedAt: serverTimestamp() }, { merge: true });
      setMetadata((prev) => ({ ...prev, [groupId]: { ...current, prLink } }));
      setSavedPrLinks((prev) => new Set([...prev, groupId]));
      setTimeout(() => setSavedPrLinks((prev) => { const s = new Set(prev); s.delete(groupId); return s; }), 2000);
    } catch (err) {
      toast.error("Failed to save PR link");
    }
  };

  // ── Copy prompt ─────────────────────────────────────────────────────────────

  const handleCopyPrompt = (groupId, prompt) => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopiedPrompts((prev) => new Set([...prev, groupId]));
      setTimeout(() => setCopiedPrompts((prev) => { const s = new Set(prev); s.delete(groupId); return s; }), 2000);
    });
  };

  // ── Row expand ──────────────────────────────────────────────────────────────

  const toggleRow = (groupId) => {
    setExpandedRows((prev) => {
      const s = new Set(prev);
      s.has(groupId) ? s.delete(groupId) : s.add(groupId);
      return s;
    });
  };

  // ── Sort ────────────────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const enrichedErrors = errors.map((e) => ({
    ...e,
    ...(metadata[e.groupId] || {}),
    status: (metadata[e.groupId]?.status) || (e.acknowledged ? "acknowledged" : "open"),
  }));

  const filteredErrors = enrichedErrors.filter((e) => {
    if (filterAssignee === "unassigned") {
      if (e.assignee) return false;
    } else if (filterAssignee !== "all" && e.assignee !== filterAssignee) {
      return false;
    }
    if (filterStatus !== "all" && e.status !== filterStatus) return false;
    if (quickFilter === "open" && e.status !== "open") return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!e.errorMessage?.toLowerCase().includes(q) && !e.service?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const effectiveSortField = quickFilter === "latest" ? "lastSeen" : sortField;
  const effectiveSortDir  = quickFilter === "latest" ? "desc" : sortDir;

  const sortedErrors = [...filteredErrors].sort((a, b) => {
    let va = a[effectiveSortField], vb = b[effectiveSortField];
    if (effectiveSortField === "count") { va = Number(va) || 0; vb = Number(vb) || 0; }
    if (effectiveSortField === "lastSeen" || effectiveSortField === "firstSeen") {
      va = new Date(va).getTime() || 0;
      vb = new Date(vb).getTime() || 0;
    }
    if (va < vb) return effectiveSortDir === "asc" ? -1 : 1;
    if (va > vb) return effectiveSortDir === "asc" ? 1 : -1;
    return 0;
  });

  const stats = {
    total:        enrichedErrors.length,
    open:         enrichedErrors.filter((e) => e.status === "open").length,
    acknowledged: enrichedErrors.filter((e) => e.status === "acknowledged").length,
    done:         enrichedErrors.filter((e) => e.status === "done").length,
    totalOccurrences: enrichedErrors.reduce((s, e) => s + (Number(e.count) || 0), 0),
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Sub-view switcher + handbook link */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-1 devtools-subview-pills">
          <button
            type="button"
            className={`devtools-subview-pill ${view === "live" ? "active" : ""}`}
            onClick={() => setView("live")}
          >
            Live errors
          </button>
          <button
            type="button"
            className={`devtools-subview-pill ${view === "weekly" ? "active" : ""}`}
            onClick={() => setView("weekly")}
          >
            📊 Weekly report
          </button>
        </div>
        <a
          href={ON_CALL_HANDBOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-secondary"
        >
          📋 On-Call Handbook ↗
        </a>
      </div>

      {view === "weekly" && <WeeklyReportTab errors={errors} metadata={metadata} />}

      {view === "live" && <>
      {/* On-Call Handbook banner */}
      <div className="alert alert-info d-flex align-items-center justify-content-between gap-3 mb-4 py-2 px-3" role="alert">
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: "1.1rem" }}>📋</span>
          <span className="fw-semibold">On-Call Incident Response Handbook</span>
          <span className="text-muted small d-none d-md-inline">— procedures, escalation paths, and runbooks</span>
        </div>
        <a
          href={ON_CALL_HANDBOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-info text-white text-nowrap"
        >
          Open Handbook ↗
        </a>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg">
          <div className="card text-center h-100 shadow-sm">
            <div className="card-body py-3">
              <div className="text-muted small text-uppercase fw-semibold">
                Total errors
              </div>
              <h2 className="mb-0 mt-1">{stats.total}</h2>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg">
          <div className="card text-center h-100 shadow-sm">
            <div className="card-body py-3">
              <div className="text-muted small text-uppercase fw-semibold">
                Occurrences
              </div>
              <h2 className="mb-0 mt-1">
                {stats.totalOccurrences.toLocaleString()}
              </h2>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg" role="button" onClick={() => setQuickFilter((f) => f === "open" ? null : "open")} title="Click to filter open errors">
          <div className={`card text-center h-100 shadow-sm ${quickFilter === "open" ? "bg-danger text-white" : "border-danger"}`} style={{ cursor: "pointer" }}>
            <div className="card-body py-3">
              <div className={`small text-uppercase fw-semibold ${quickFilter === "open" ? "text-white" : "text-muted"}`}>
                Open {quickFilter === "open" ? "✓" : ""}
              </div>
              <h2 className={`mb-0 mt-1 ${quickFilter === "open" ? "text-white" : "text-danger"}`}>{stats.open}</h2>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg">
          <div className="card text-center border-primary h-100 shadow-sm">
            <div className="card-body py-3">
              <div className="text-muted small text-uppercase fw-semibold">
                Acknowledged
              </div>
              <h2 className="mb-0 mt-1 text-primary">{stats.acknowledged}</h2>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg">
          <div className="card text-center border-success h-100 shadow-sm">
            <div className="card-body py-3">
              <div className="text-muted small text-uppercase fw-semibold">
                Done
              </div>
              <h2 className="mb-0 mt-1 text-success">{stats.done}</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-white py-3">
          <div className="row align-items-center g-3">
            <div className="col-md-4">
              <label className="form-label small text-muted mb-1 d-block">
                Time range (GCP)
              </label>
              <div
                className="btn-group btn-group-sm flex-wrap devtools-period-group"
                role="group"
              >
                {TIME_PERIODS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`btn btn-outline-primary ${timePeriod === p.value ? "active" : ""}`}
                    onClick={() => handlePeriodChange(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-md-2">
              <label htmlFor="devtools-assignee" className="form-label small text-muted mb-1">
                Assignee
              </label>
              <select
                id="devtools-assignee"
                className="form-select form-select-sm"
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
              >
                <option value="all">All</option>
                <option value="unassigned">Unassigned</option>
                {DEVS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label htmlFor="devtools-status" className="form-label small text-muted mb-1">
                Status
              </label>
              <select
                id="devtools-status"
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="devtools-search" className="form-label small text-muted mb-1">
                Search
              </label>
              <input
                id="devtools-search"
                type="search"
                className="form-control form-control-sm"
                placeholder="Message or service…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1 d-block">
                Quick filters
              </label>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${quickFilter === "open" ? "btn-danger" : "btn-outline-danger"}`}
                  onClick={() => setQuickFilter((f) => f === "open" ? null : "open")}
                  title="Show only open errors"
                >
                  🔴 Open only
                </button>
                <button
                  type="button"
                  className={`btn ${quickFilter === "latest" ? "btn-warning" : "btn-outline-warning"}`}
                  onClick={() => setQuickFilter((f) => f === "latest" ? null : "latest")}
                  title="Sort by most recently seen"
                >
                  🕐 Latest
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body py-2 border-top bg-light">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <small className="text-muted d-flex align-items-center gap-2">
              <span
                className={`devtools-refresh-dot ${refreshing ? "refreshing" : ""}`}
                aria-hidden
              />
              {lastRefreshed
                ? `Last refresh ${lastRefreshed.toLocaleTimeString()} · auto-refresh in ${countdown}s`
                : "Loading…"}
            </small>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleManualRefresh}
              disabled={loading || refreshing}
            >
              Refresh now
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="text-muted mb-0">Fetching errors from GCP…</p>
        </div>
      ) : fetchError ? (
        <div className="alert alert-danger" role="alert">
          <strong>Could not load errors.</strong> {fetchError}
          {fetchErrorHelpUrl && (
            <div className="mt-2">
              <a
                href={fetchErrorHelpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="alert-link"
              >
                Enable Error Reporting API in Google Cloud
              </a>
            </div>
          )}
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleManualRefresh}
            >
              Try again
            </button>
          </div>
        </div>
      ) : sortedErrors.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center text-muted py-5">
            No errors match the current filters.
          </div>
        </div>
      ) : (
        <div className="card shadow-sm devtools-table-wrap" style={{ overflowX: "auto" }}>
          <div className="table-responsive">
            <table className="table table-hover table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col" style={{ width: "2.5rem" }}>#</th>
                  <th
                    scope="col"
                    role="button"
                    className="user-select-none"
                    onClick={() => handleSort("errorMessage")}
                  >
                    Error{" "}
                    {sortField === "errorMessage" && (
                      <span className="text-muted">{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th
                    scope="col"
                    role="button"
                    className="user-select-none text-end"
                    style={{ width: "5.5rem" }}
                    onClick={() => handleSort("count")}
                  >
                    Count{" "}
                    {sortField === "count" && (
                      <span className="text-muted">{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th
                    scope="col"
                    role="button"
                    className="user-select-none text-nowrap"
                    style={{ width: "9rem" }}
                    onClick={() => handleSort("lastSeen")}
                  >
                    Last seen{" "}
                    {sortField === "lastSeen" && (
                      <span className="text-muted">{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                  <th scope="col" style={{ width: "9rem" }}>Assignee</th>
                  <th scope="col" style={{ width: "8rem" }}>Status</th>
                  <th scope="col" style={{ width: "10rem" }}>
                    PR link
                    <span className="text-muted fw-normal" style={{ fontSize: "0.7rem" }}> (click to edit)</span>
                  </th>
                  <th scope="col">
                    Notes
                    <span className="text-muted fw-normal" style={{ fontSize: "0.7rem" }}> (auto-saved)</span>
                  </th>
                  <th scope="col" style={{ width: "4.5rem" }}>GCP</th>
                  <th scope="col" style={{ width: "5rem" }} />
                </tr>
              </thead>
              <tbody>
                {sortedErrors.map((error, idx) => (
                  <ErrorRow
                    key={error.groupId}
                    idx={idx}
                    error={error}
                    expanded={expandedRows.has(error.groupId)}
                    onToggle={() => toggleRow(error.groupId)}
                    onAssigneeChange={handleAssigneeChange}
                    onStatusCycle={handleStatusCycle}
                    onCopyPrompt={handleCopyPrompt}
                    onSaveNotes={handleSaveNotes}
                    onSavePrLink={handleSavePrLink}
                    isCopied={copiedPrompts.has(error.groupId)}
                    isSaved={savedNotes.has(error.groupId)}
                    isPrSaved={savedPrLinks.has(error.groupId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>}
    </>
  );
}

// ─── Copy Org Tab ─────────────────────────────────────────────────────────────

const DEV_TEAM_EMAILS = [
  "joao@addy-ai.com",
  "jmadeira@addy-ai.com",
  "bhaulik@addy-ai.com",
  "shashank@addy-ai.com",
  "michael@addy-ai.com",
];

function CopyOrgTab() {
  const [prodOrgId, setProdOrgId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleClone = async () => {
    if (!prodOrgId.trim()) {
      toast.error("Enter a production org ID.");
      return;
    }

    setRunning(true);
    setResult(null);

    try {
      const baseUrl = window.location.hostname.includes("localhost") ? apiUrlDevFirebase : cloudRunApiUrl
      const res = await fetch(`${baseUrl}/admin/clone-org-prod-to-dev`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prodOrgId: prodOrgId.trim() }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.reason || `HTTP ${res.status}`);
      }

      setResult(data);
      toast.success(`Org cloned → ${data.newOrgId}`);
    } catch (err) {
      toast.error(`Clone failed: ${err.message}`);
      setResult({ error: err.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      {/* Info banner */}
      <div className="alert alert-warning d-flex gap-2 align-items-start mb-4" role="alert">
        <span style={{ fontSize: "1.1rem" }}>⚠️</span>
        <div>
          <strong>This clones an entire organization from production to development.</strong>
          <br />
          Includes: org document, all loans (with borrowers, documents, storage
          files, checklist tasks, needed docs, contacts), checklist templates,
          and org members. After cloning, the dev team is automatically added as
          accepted admins.
        </div>
      </div>

      {/* Input card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <span className="fw-semibold">Organization to clone</span>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">
                Prod org ID
              </label>
              <input
                type="text"
                className="form-control font-monospace"
                placeholder="e.g. aBcD1234prodOrgId"
                value={prodOrgId}
                onChange={(e) => setProdOrgId(e.target.value)}
                disabled={running}
              />
            </div>
            <div className="col-md-auto">
              <button
                type="button"
                className="btn btn-warning fw-semibold"
                onClick={handleClone}
                disabled={running || !prodOrgId.trim()}
              >
                {running ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Cloning org…
                  </>
                ) : (
                  "Clone org to dev"
                )}
              </button>
            </div>
          </div>

          {/* Dev team preview */}
          <div className="mt-4">
            <p className="small text-muted mb-2 fw-semibold text-uppercase">
              Dev team added automatically
            </p>
            <div className="d-flex flex-wrap gap-2">
              {DEV_TEAM_EMAILS.map((email) => (
                <span key={email} className="badge bg-light text-dark border font-monospace">
                  {email}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && !result.error && (
        <div className="card shadow-sm border-success">
          <div className="card-header bg-success bg-opacity-10 py-3 d-flex justify-content-between align-items-center">
            <span className="fw-semibold text-success">Clone complete</span>
            <span className="text-muted small">{result.duration}</span>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <p className="small text-muted mb-1 text-uppercase fw-semibold">
                  New dev org ID
                </p>
                <code className="fs-6 text-success">{result.newOrgId}</code>
              </div>
              <div className="col-md-6">
                <p className="small text-muted mb-1 text-uppercase fw-semibold">
                  Org name
                </p>
                <span className="fw-medium">[DEV COPY] {result.orgName}</span>
              </div>
            </div>

            {/* Stats grid */}
            <div className="row g-2 mb-4">
              {[
                { label: "Loans", value: result.stats?.loansCloned, icon: "📋" },
                { label: "Borrowers", value: result.stats?.borrowersCloned, icon: "👤" },
                { label: "Documents", value: result.stats?.documentsCloned, icon: "📄" },
                { label: "Storage files", value: result.stats?.storageFilesCopied, icon: "🗂" },
                { label: "Checklist tasks", value: result.stats?.processingTasksCloned, icon: "✅" },
                { label: "Needed docs", value: result.stats?.neededDocumentsCloned, icon: "📎" },
                { label: "Contacts", value: result.stats?.contactsCloned, icon: "📇" },
                { label: "Templates", value: result.stats?.checklistTemplatesCloned, icon: "🗒" },
                { label: "Members copied", value: result.stats?.membersCloned, icon: "👥" },
              ].map(({ label, value, icon }) => (
                <div key={label} className="col-6 col-md-4 col-lg-3">
                  <div className="card text-center h-100 border-0 bg-light">
                    <div className="card-body py-2 px-2">
                      <div className="small text-muted">{icon} {label}</div>
                      <div className="fw-bold fs-5">{value ?? "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dev team result */}
            {result.devTeam && (
              <div className="mb-3">
                <p className="small text-muted mb-2 fw-semibold text-uppercase">
                  Dev team members added
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {result.devTeam.added?.map((email) => (
                    <span key={email} className="badge bg-success font-monospace">
                      ✓ {email}
                    </span>
                  ))}
                  {result.devTeam.skipped?.map((email) => (
                    <span key={email} className="badge bg-secondary font-monospace">
                      already exists: {email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Loan-level results */}
            {result.loanResults?.length > 0 && (
              <div>
                <p className="small text-muted mb-2 fw-semibold text-uppercase">
                  Loan mapping
                </p>
                <div className="table-responsive">
                  <table className="table table-sm copy-loan-table mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Prod loan ID</th>
                        <th>Dev loan ID</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.loanResults.map((lr) => (
                        <tr key={lr.loanId}>
                          <td className="font-monospace small text-muted">{lr.loanId}</td>
                          <td className="font-monospace small text-success">
                            {lr.newLoanId || "—"}
                          </td>
                          <td>
                            {lr.success ? (
                              <span className="badge bg-success">Done</span>
                            ) : (
                              <span className="badge bg-danger" title={lr.error}>
                                Error
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Non-fatal errors */}
            {result.stats?.errors?.length > 0 && (
              <div className="alert alert-warning mt-3 mb-0 py-2">
                <strong>Non-fatal warnings ({result.stats.errors.length}):</strong>
                <ul className="mb-0 mt-1 small">
                  {result.stats.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {result?.error && (
        <div className="alert alert-danger" role="alert">
          <strong>Clone failed:</strong> {result.error}
        </div>
      )}
    </>
  );
}

// ─── Copy Loan Tab ────────────────────────────────────────────────────────────

const EMPTY_JOB = { prodOrgId: "", loanId: "", devOrgId: "" };

function CopyLoanTab() {
  const [jobs, setJobs] = useState([{ ...EMPTY_JOB, id: Date.now() }]);
  const [globalDevOrgId, setGlobalDevOrgId] = useState("");
  const [applyGlobalDevOrg, setApplyGlobalDevOrg] = useState(false);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);

  // ── Job management ──────────────────────────────────────────────────────────

  const addJob = () =>
    setJobs((prev) => [
      ...prev,
      {
        ...EMPTY_JOB,
        id: Date.now(),
        devOrgId: applyGlobalDevOrg ? globalDevOrgId : "",
      },
    ]);

  const removeJob = (id) =>
    setJobs((prev) => prev.filter((j) => j.id !== id));

  const updateJob = (id, field, value) =>
    setJobs((prev) =>
      prev.map((j) => (j.id === id ? { ...j, [field]: value } : j))
    );

  const applyGlobalDevOrgToAll = () => {
    if (!globalDevOrgId.trim()) return;
    setJobs((prev) => prev.map((j) => ({ ...j, devOrgId: globalDevOrgId.trim() })));
    setApplyGlobalDevOrg(true);
  };

  // ── Run clones ──────────────────────────────────────────────────────────────

  const runClones = async () => {
    const validJobs = jobs.filter(
      (j) => j.prodOrgId.trim() && j.loanId.trim() && j.devOrgId.trim()
    );

    if (validJobs.length === 0) {
      toast.error("Fill in at least one complete row before running.");
      return;
    }

    setRunning(true);
    setResults([]);

    const newResults = validJobs.map((j) => ({
      id: j.id,
      prodOrgId: j.prodOrgId.trim(),
      loanId: j.loanId.trim(),
      devOrgId: j.devOrgId.trim(),
      status: "pending",
      newLoanId: null,
      stats: null,
      duration: null,
      error: null,
    }));
    setResults(newResults);

    for (let i = 0; i < newResults.length; i++) {
      const job = newResults[i];

      setResults((prev) =>
        prev.map((r) => (r.id === job.id ? { ...r, status: "running" } : r))
      );

      try {
        const baseUrl = window.location.hostname.includes("localhost") ? apiUrlDevFirebase : cloudRunApiUrl
        const res = await fetch(`${baseUrl}/admin/clone-loan-prod-to-dev`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prodOrgId: job.prodOrgId,
            loanId: job.loanId,
            devOrgId: job.devOrgId,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          throw new Error(data.reason || `HTTP ${res.status}`);
        }

        setResults((prev) =>
          prev.map((r) =>
            r.id === job.id
              ? {
                  ...r,
                  status: "done",
                  newLoanId: data.newLoanId,
                  stats: data.stats,
                  duration: data.duration,
                }
              : r
          )
        );
        toast.success(`Loan ${job.loanId} cloned → ${data.newLoanId}`);
      } catch (err) {
        setResults((prev) =>
          prev.map((r) =>
            r.id === job.id ? { ...r, status: "error", error: err.message } : r
          )
        );
        toast.error(`Loan ${job.loanId}: ${err.message}`);
      }
    }

    setRunning(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const hasResults = results.length > 0;

  return (
    <>
      {/* Info banner */}
      <div className="alert alert-info d-flex gap-2 align-items-start mb-4" role="alert">
        <span style={{ fontSize: "1.1rem" }}>ℹ️</span>
        <div>
          <strong>Clone loan(s) from production to development.</strong>
          <br />
          Each row clones one loan — including borrowers, documents (with
          storage files), processing checklist tasks, needed documents, and
          loan contacts.
        </div>
      </div>

      {/* Global dev org shortcut */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <span className="fw-semibold">Shared dev organization ID</span>
          <span className="text-muted small ms-2">
            (optional — fills all rows at once)
          </span>
        </div>
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">
                Dev org ID
              </label>
              <input
                type="text"
                className="form-control form-control-sm font-monospace"
                placeholder="e.g. abc123devOrgId"
                value={globalDevOrgId}
                onChange={(e) => setGlobalDevOrgId(e.target.value)}
              />
            </div>
            <div className="col-md-auto">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={applyGlobalDevOrgToAll}
                disabled={!globalDevOrgId.trim()}
              >
                Apply to all rows
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loan rows */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Loans to clone</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addJob}
            disabled={running}
          >
            + Add loan
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 copy-loan-table">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>Prod org ID</th>
                  <th>Loan ID</th>
                  <th>Dev org ID</th>
                  <th style={{ width: "3rem" }} />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, idx) => (
                  <tr key={job.id}>
                    <td className="ps-3 text-muted small">{idx + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="prodOrgId"
                        value={job.prodOrgId}
                        onChange={(e) =>
                          updateJob(job.id, "prodOrgId", e.target.value)
                        }
                        disabled={running}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="loanId"
                        value={job.loanId}
                        onChange={(e) =>
                          updateJob(job.id, "loanId", e.target.value)
                        }
                        disabled={running}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="devOrgId"
                        value={job.devOrgId}
                        onChange={(e) =>
                          updateJob(job.id, "devOrgId", e.target.value)
                        }
                        disabled={running}
                      />
                    </td>
                    <td>
                      {jobs.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeJob(job.id)}
                          disabled={running}
                          title="Remove row"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white py-3 d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={runClones}
            disabled={running}
          >
            {running ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Cloning…
              </>
            ) : (
              `Clone ${jobs.filter((j) => j.prodOrgId && j.loanId && j.devOrgId).length || jobs.length} loan(s)`
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {hasResults && (
        <div className="card shadow-sm">
          <div className="card-header bg-white py-3">
            <span className="fw-semibold">Results</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table align-middle mb-0 copy-loan-table">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Loan ID (prod)</th>
                    <th>Prod org</th>
                    <th>Dev org</th>
                    <th>Status</th>
                    <th>New loan ID (dev)</th>
                    <th>Stats</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id}>
                      <td className="ps-3 font-monospace small">{r.loanId}</td>
                      <td className="font-monospace small text-muted">
                        {r.prodOrgId}
                      </td>
                      <td className="font-monospace small text-muted">
                        {r.devOrgId}
                      </td>
                      <td>
                        <CopyLoanStatusBadge status={r.status} />
                      </td>
                      <td className="font-monospace small">
                        {r.newLoanId ? (
                          <span className="text-success fw-semibold">
                            {r.newLoanId}
                          </span>
                        ) : r.error ? (
                          <span className="text-danger small">{r.error}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {r.stats ? (
                          <div className="copy-loan-stats">
                            <span title="Borrowers">👤 {r.stats.borrowersCloned}</span>
                            <span title="Documents">📄 {r.stats.documentsCloned}</span>
                            <span title="Storage files">🗂 {r.stats.storageFilesCopied + r.stats.taskFilesCopied}</span>
                            <span title="Checklist tasks">✅ {r.stats.processingTasksCloned}</span>
                            <span title="Contacts">📇 {r.stats.contactsCloned}</span>
                            {r.stats.errors?.length > 0 && (
                              <span
                                className="text-warning"
                                title={r.stats.errors.join(", ")}
                              >
                                ⚠ {r.stats.errors.length} warn
                              </span>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-muted small">{r.duration || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CopyLoanStatusBadge({ status }) {
  const map = {
    pending: { cls: "bg-secondary", label: "Pending" },
    running: { cls: "bg-primary",   label: "Running…" },
    done:    { cls: "bg-success",   label: "Done" },
    error:   { cls: "bg-danger",    label: "Error" },
  };
  const { cls, label } = map[status] || map.pending;
  return (
    <span className={`badge ${cls}`}>{label}</span>
  );
}

// ─── TruncatedText ────────────────────────────────────────────────────────────

function TruncatedText({ text, limit = 100 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= limit) return <span>{text}</span>;
  return (
    <span>
      {expanded ? text : text.slice(0, limit).trimEnd()}
      {!expanded && <span className="text-muted">… </span>}
      <button
        type="button"
        className="devtools-show-more-btn"
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
      >
        {expanded ? "show less" : "show more"}
      </button>
    </span>
  );
}

// ─── Error Row ────────────────────────────────────────────────────────────────

function ErrorRow({
  idx, error, expanded, onToggle,
  onAssigneeChange, onStatusCycle, onCopyPrompt, onSaveNotes, onSavePrLink,
  isCopied, isSaved, isPrSaved,
}) {
  const [localNotes, setLocalNotes]   = useState(error.notes   || "");
  const [localPrLink, setLocalPrLink] = useState(error.prLink  || "");
  const [prEditing, setPrEditing]     = useState(false);

  // Sync from Firestore metadata updates
  useEffect(() => { setLocalNotes(error.notes   || ""); }, [error.notes]);
  useEffect(() => { setLocalPrLink(error.prLink || ""); }, [error.prLink]);

  const statusInfo = STATUS_LABELS[error.status] || STATUS_LABELS.open;

  const cursorPrompt = generateCursorPrompt({
    errorMessage: error.errorMessage,
    stackTrace:   error.stackTrace,
    service:      error.service,
    firstSeen:    error.firstSeen,
    lastSeen:     error.lastSeen,
    count:        error.count,
    consoleUrl:   error.consoleUrl,
  });

  const commitPrLink = () => {
    setPrEditing(false);
    if (localPrLink !== (error.prLink || "")) {
      onSavePrLink(error.groupId, localPrLink);
    }
  };

  const commitNotes = () => {
    if (localNotes !== (error.notes || "")) {
      onSaveNotes(error.groupId, localNotes);
    }
  };

  return (
    <>
      <tr className={expanded ? "table-active" : ""}>
        {/* # */}
        <td className="text-muted small align-middle">{idx + 1}</td>

        {/* Error message + service */}
        <td className="devtools-error-cell align-middle">
          <div className="fw-medium">
            <TruncatedText text={error.errorMessage} limit={100} />
          </div>
          {error.service && (
            <div className="small text-muted mt-1">{error.service}</div>
          )}
        </td>

        {/* Count */}
        <td className="text-end align-middle">
          <span className={getCountClass(Number(error.count) || 0)}>
            {(Number(error.count) || 0).toLocaleString()}
          </span>
        </td>

        {/* Last seen */}
        <td className="text-nowrap small text-muted align-middle">{error.lastSeen || "—"}</td>

        {/* Assignee — inline select only, no avatar below */}
        <td className="align-middle">
          <select
            className="form-select form-select-sm"
            value={error.assignee || ""}
            onChange={(e) => onAssigneeChange(error.groupId, e.target.value)}
            style={error.assignee ? { borderLeft: `3px solid ${devColor(error.assignee)}` } : {}}
          >
            <option value="">Unassigned</option>
            {DEVS.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
        </td>

        {/* Status — cycle button */}
        <td className="align-middle">
          <button
            type="button"
            className={`btn btn-sm devtools-status-btn text-white ${statusInfo.btnClass}`}
            onClick={() => onStatusCycle(error.groupId, error.status)}
            title="Click to advance status"
          >
            {statusInfo.label}
          </button>
        </td>

        {/* PR link — inline editable */}
        <td className="align-middle" style={{ minWidth: "160px" }}>
          {prEditing ? (
            <div className="input-group input-group-sm">
              <input
                type="url"
                className="form-control form-control-sm"
                value={localPrLink}
                autoFocus
                onChange={(e) => setLocalPrLink(e.target.value)}
                onBlur={commitPrLink}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitPrLink();
                  if (e.key === "Escape") { setLocalPrLink(error.prLink || ""); setPrEditing(false); }
                }}
                placeholder="github.com/…/pull/123"
              />
              {isPrSaved && (
                <span className="input-group-text text-success small">✓</span>
              )}
            </div>
          ) : (
            <div
              className="devtools-pr-cell-view d-flex align-items-center gap-1"
              onClick={() => setPrEditing(true)}
              title="Click to add/edit PR link"
            >
              {localPrLink ? (
                <>
                  <a
                    href={localPrLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-success small fw-medium text-decoration-none"
                    onClick={(e) => e.stopPropagation()}
                    title={localPrLink}
                  >
                    PR ↗
                  </a>
                  <span className="text-muted small" style={{ fontSize: "0.7rem" }}>✏️</span>
                </>
              ) : (
                <span className="text-muted small fst-italic">+ add PR</span>
              )}
            </div>
          )}
        </td>

        {/* Notes — inline editable textarea */}
        <td className="align-middle" style={{ minWidth: "200px" }}>
          <textarea
            className="form-control form-control-sm devtools-inline-notes"
            rows={2}
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            onBlur={commitNotes}
            placeholder="Notes…"
            title="Investigation notes — auto-saved on blur"
          />
          {isSaved && (
            <div className="text-success" style={{ fontSize: "0.65rem", marginTop: "2px" }}>✓ Saved</div>
          )}
        </td>

        {/* GCP link */}
        <td className="align-middle">
          <a
            href={error.consoleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-link btn-sm p-0"
          >
            GCP ↗
          </a>
        </td>

        {/* Stack / prompt toggle */}
        <td className="text-end align-middle">
          <button
            type="button"
            className={`btn btn-sm ${expanded ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={onToggle}
            title="View stack trace & Cursor prompt"
          >
            {expanded ? "Hide" : "Stack"}
          </button>
        </td>
      </tr>

      {/* Expanded: stack trace + Cursor prompt only */}
      {expanded && (
        <tr className="table-light">
          <td colSpan={10} className="p-0 border-0">
            <div className="p-3 p-md-4 bg-white border-top">
              <div className="row g-4">
                <div className="col-lg-6">
                  <h6 className="text-uppercase text-muted small fw-semibold mb-2">Stack trace</h6>
                  <pre className="devtools-pre-block devtools-pre-mono mb-0">
                    {error.stackTrace || "No stack trace available."}
                  </pre>
                </div>
                <div className="col-lg-6">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="text-uppercase text-muted small fw-semibold mb-0">Cursor fix prompt</h6>
                    <button
                      type="button"
                      className={`btn btn-sm ${isCopied ? "btn-success" : "btn-outline-secondary"}`}
                      onClick={() => onCopyPrompt(error.groupId, cursorPrompt)}
                    >
                      {isCopied ? "Copied ✓" : "Copy"}
                    </button>
                  </div>
                  <pre className="devtools-pre-block mb-0">{cursorPrompt}</pre>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Weekly Report Tab ────────────────────────────────────────────────────────

const WEEK_OPTIONS = [
  { label: "This week",   offset: 0 },
  { label: "Last week",   offset: 1 },
  { label: "2 weeks ago", offset: 2 },
  { label: "3 weeks ago", offset: 3 },
];

function getWeekBounds(offsetWeeks = 0) {
  const now    = new Date();
  const day    = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7) - offsetWeeks * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday, end: sunday };
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = d instanceof Date ? d : (d?.toDate ? d.toDate() : new Date(d));
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateShort(d) {
  if (!d) return "—";
  const dt = d instanceof Date ? d : (d?.toDate ? d.toDate() : new Date(d));
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Helper: load Firestore docs for a date range ─────────────────────────────
async function loadDocsForRange(rangeStart, rangeEnd) {
  try {
    const snap = await getDocs(
      query(
        collection(firestoreProd, FIRESTORE_COLLECTION),
        where("updatedAt", ">=", Timestamp.fromDate(rangeStart)),
        where("updatedAt", "<=", Timestamp.fromDate(rangeEnd)),
        orderBy("updatedAt", "desc")
      )
    );
    const docs = [];
    snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
    return docs;
  } catch {
    // Fallback: load all + client-side filter
    const snap = await getDocs(
      query(collection(firestoreProd, FIRESTORE_COLLECTION), orderBy("updatedAt", "desc"))
    );
    const docs = [];
    snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
    return docs.filter((d) => {
      if (!d.updatedAt) return false;
      const ts = d.updatedAt?.toDate ? d.updatedAt.toDate() : new Date(d.updatedAt);
      return ts >= rangeStart && ts <= rangeEnd;
    });
  }
}

// ── Helper: compute stats from a list of enriched docs ───────────────────────
function computeStats(enriched) {
  const total            = enriched.length;
  const done             = enriched.filter((e) => e.status === "done").length;
  const acknowledged     = enriched.filter((e) => e.status === "acknowledged").length;
  const open             = enriched.filter((e) => e.status === "open").length;
  const withPr           = enriched.filter((e) => e.prLink).length;
  const totalOccurrences = enriched.reduce((s, e) => s + (Number(e.count) || 0), 0);
  // Done rate = only fully resolved (done) out of all touched this week
  const doneRate       = total > 0 ? Math.round((done / total) * 100) : 0;
  // Handled rate = done + acknowledged out of total (how much was at least looked at)
  const handledRate    = total > 0 ? Math.round(((done + acknowledged) / total) * 100) : 0;
  const avgOccurrences = total > 0 ? Math.round(totalOccurrences / total) : 0;
  return { total, done, acknowledged, open, withPr, totalOccurrences, doneRate, handledRate, avgOccurrences };
}

// ── Helper: delta chip ────────────────────────────────────────────────────────
function DeltaChip({ current, prev, lowerIsBetter = false }) {
  if (prev === null || prev === undefined) return null;
  const diff = current - prev;
  if (diff === 0) return <span className="devtools-delta devtools-delta-neutral">= same</span>;
  const up = diff > 0;
  // For "lower is better" metrics (open, occurrences): up is bad, down is good
  const good = lowerIsBetter ? !up : up;
  return (
    <span className={`devtools-delta ${good ? "devtools-delta-good" : "devtools-delta-bad"}`}>
      {up ? "↑" : "↓"} {Math.abs(diff).toLocaleString()} vs prev
    </span>
  );
}

// Receives live errors + metadata already loaded by ErrorReportingTab
function WeeklyReportTab({ errors = [] }) {
  const [weekOffset, setWeekOffset]       = useState(0);
  const [currentDocs, setCurrentDocs]     = useState([]);
  const [prevDocs, setPrevDocs]           = useState([]);
  const [fsLoading, setFsLoading]         = useState(true);
  const [exportCopied, setExportCopied]   = useState(false);

  const { start, end }         = getWeekBounds(weekOffset);
  const { start: ps, end: pe } = getWeekBounds(weekOffset + 1); // previous week

  // ── Load current + previous week in parallel ─────────────────────────────────
  const loadData = useCallback(async () => {
    setFsLoading(true);
    try {
      const [cur, prev] = await Promise.all([
        loadDocsForRange(start, end),
        loadDocsForRange(ps, pe),
      ]);
      setCurrentDocs(cur);
      setPrevDocs(prev);
    } catch (e) {
      console.error("Failed to load weekly docs:", e);
      setCurrentDocs([]);
      setPrevDocs([]);
    } finally {
      setFsLoading(false);
    }
  }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Enrich with live GCP data (for current week only) ────────────────────────
  const liveGcpMap = Object.fromEntries(errors.map((e) => [e.groupId, e]));

  const enrich = (docs) => docs.map((doc) => ({
    ...doc,
    ...(liveGcpMap[doc.id] || {}),
    assignee: doc.assignee,
    status:   doc.status || (liveGcpMap[doc.id]?.acknowledged ? "acknowledged" : "open"),
    notes:    doc.notes,
    prLink:   doc.prLink,
    groupId:  doc.id,
  }));

  const enriched     = enrich(currentDocs);
  const enrichedPrev = enrich(prevDocs);

  const stats     = computeStats(enriched);
  const prevStats = computeStats(enrichedPrev);

  // ── Per-dev breakdown ────────────────────────────────────────────────────────
  const devStats = DEVS.map((dev) => {
    const items     = enriched.filter((e) => e.assignee === dev.id);
    const prevItems = enrichedPrev.filter((e) => e.assignee === dev.id);
    return {
      ...dev,
      total:        items.length,
      prevTotal:    prevItems.length,
      done:         items.filter((e) => e.status === "done").length,
      acknowledged: items.filter((e) => e.status === "acknowledged").length,
      open:         items.filter((e) => e.status === "open").length,
      withPr:       items.filter((e) => e.prLink).length,
      items,
    };
  }).filter((d) => d.total > 0 || d.prevTotal > 0);

  const unassigned = enriched.filter((e) => !e.assignee);

  // ── Markdown export ──────────────────────────────────────────────────────────
  const buildMarkdown = () => {
    const s = stats;
    const lines = [
      `# On-Call Weekly Report`,
      `**Week:** ${fmtDate(start)} – ${fmtDate(end)}`,
      `**Generated:** ${new Date().toLocaleString()}`,
      ``,
      `## Summary`,
      `| Metric | This week | Prev week | Δ |`,
      `|--------|-----------|-----------|---|`,
      `| Errors touched | ${s.total} | ${prevStats.total} | ${s.total - prevStats.total >= 0 ? "+" : ""}${s.total - prevStats.total} |`,
      `| Occurrences | ${s.totalOccurrences.toLocaleString()} | ${prevStats.totalOccurrences.toLocaleString()} | — |`,
      `| Resolved (done) | ${s.done} | ${prevStats.done} | ${s.done - prevStats.done >= 0 ? "+" : ""}${s.done - prevStats.done} |`,
      `| Acknowledged | ${s.acknowledged} | ${prevStats.acknowledged} | ${s.acknowledged - prevStats.acknowledged >= 0 ? "+" : ""}${s.acknowledged - prevStats.acknowledged} |`,
      `| Still open | ${s.open} | ${prevStats.open} | ${s.open - prevStats.open >= 0 ? "+" : ""}${s.open - prevStats.open} |`,
      `| Done rate | ${s.doneRate}% | ${prevStats.doneRate}% | ${s.doneRate - prevStats.doneRate >= 0 ? "+" : ""}${s.doneRate - prevStats.doneRate}pp |`,
      `| Handled rate (done+ack) | ${s.handledRate}% | ${prevStats.handledRate}% | — |`,
      `| With PR/Fix | ${s.withPr} | ${prevStats.withPr} | — |`,
      ``,
      `## By Developer`,
    ];
    devStats.forEach((dev) => {
      if (!dev.total) return;
      lines.push(``, `### ${dev.label}`, `- Assigned: ${dev.total}  Done: ${dev.done}  Ack: ${dev.acknowledged}  Open: ${dev.open}  PRs: ${dev.withPr}`);
      if (dev.items.length) {
        lines.push(``, `| Error | Status | PR | Updated |`, `|-------|--------|----|---------|`);
        dev.items.forEach((e) => {
          const msg = (e.errorMessage || e.id || "—").replace(/\|/g, "\\|").slice(0, 90);
          const pr  = e.prLink ? `[PR](${e.prLink})` : "—";
          const upd = e.updatedAt?.toDate ? fmtDateShort(e.updatedAt.toDate()) : "—";
          lines.push(`| ${msg} | ${e.status || "open"} | ${pr} | ${upd} |`);
        });
      }
    });
    if (unassigned.length) {
      lines.push(``, `## Unassigned (${unassigned.length})`, `| Error | Status | Updated |`, `|-------|--------|---------|`);
      unassigned.forEach((e) => {
        const msg = (e.errorMessage || e.id || "—").replace(/\|/g, "\\|").slice(0, 90);
        const upd = e.updatedAt?.toDate ? fmtDateShort(e.updatedAt.toDate()) : "—";
        lines.push(`| ${msg} | ${e.status || "open"} | ${upd} |`);
      });
    }
    return lines.join("\n");
  };

  const handleExport = () => {
    navigator.clipboard.writeText(buildMarkdown()).then(() => {
      setExportCopied(true);
      setTimeout(() => setExportCopied(false), 2500);
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Toolbar ── */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h5 className="mb-0 fw-semibold">Weekly On-Call Report</h5>
          <div className="text-muted small mt-1">
            {fmtDate(start)} — {fmtDate(end)}
            <span className="ms-2 text-muted" style={{ fontSize: "0.72rem" }}>
              vs {fmtDate(ps)} – {fmtDate(pe)}
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="btn-group btn-group-sm" role="group">
            {WEEK_OPTIONS.map((w, i) => (
              <button
                key={i}
                type="button"
                className={`btn ${weekOffset === i ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setWeekOffset(i)}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`btn btn-sm ${exportCopied ? "btn-success" : "btn-outline-secondary"}`}
            onClick={handleExport}
            disabled={fsLoading || stats.total === 0}
          >
            {exportCopied ? "Copied ✓" : "Copy as Markdown"}
          </button>
        </div>
      </div>

      {fsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="text-muted mb-0">Loading weekly data…</p>
        </div>
      ) : (
        <>
          {/* ── Summary stat cards with WoW deltas ── */}
          <div className="row g-3 mb-5">
            {[
              {
                label: "Errors touched",
                value: stats.total,
                prev: prevStats.total, rawValue: stats.total,
                color: null, border: null, lowerIsBetter: false,
                sub: stats.total > 0 ? `avg ${stats.avgOccurrences.toLocaleString()} occ/error` : null,
              },
              {
                label: "Occurrences",
                value: stats.totalOccurrences.toLocaleString(),
                prev: prevStats.totalOccurrences, rawValue: stats.totalOccurrences,
                color: null, border: null, lowerIsBetter: true,
                sub: null,
              },
              {
                label: "Resolved (done)",
                value: stats.done,
                prev: prevStats.done, rawValue: stats.done,
                color: "#198754", border: "#198754", lowerIsBetter: false,
                sub: stats.total > 0 ? `${stats.done} of ${stats.total} touched` : null,
              },
              {
                label: "Acknowledged",
                value: stats.acknowledged,
                prev: prevStats.acknowledged, rawValue: stats.acknowledged,
                color: "#0d6efd", border: "#0d6efd", lowerIsBetter: false,
                sub: stats.acknowledged > 0 ? "in progress / not yet done" : null,
              },
              {
                label: "Still open",
                value: stats.open,
                prev: prevStats.open, rawValue: stats.open,
                color: stats.open > 0 ? "#dc3545" : "#6c757d", border: stats.open > 0 ? "#dc3545" : null, lowerIsBetter: true,
                sub: stats.open > 0 ? "needs attention" : "all handled",
              },
              {
                label: "Done rate",
                value: `${stats.doneRate}%`,
                prev: prevStats.doneRate, rawValue: stats.doneRate,
                color: stats.doneRate >= 80 ? "#198754" : stats.doneRate >= 50 ? "#fd7e14" : "#dc3545",
                border: null, lowerIsBetter: false,
                sub: `handled rate ${stats.handledRate}% (done + ack)`,
              },
              {
                label: "With PR / Fix",
                value: stats.withPr,
                prev: prevStats.withPr, rawValue: stats.withPr,
                color: "#6f42c1", border: "#6f42c1", lowerIsBetter: false,
                sub: null,
              },
            ].map((s) => (
              <div key={s.label} className="col-6 col-sm-4 col-lg">
                <div
                  className="card h-100 shadow-sm devtools-stat-card"
                  style={s.border ? { borderTop: `3px solid ${s.border}` } : {}}
                >
                  <div className="card-body py-3 px-3">
                    <div className="devtools-stat-label">{s.label}</div>
                    <div className="devtools-stat-value" style={{ color: s.color || "inherit" }}>
                      {s.value}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                      <DeltaChip
                        current={s.rawValue !== undefined ? s.rawValue : (typeof s.value === "number" ? s.value : parseInt(s.value) || 0)}
                        prev={s.prev}
                        lowerIsBetter={s.lowerIsBetter}
                      />
                    </div>
                    {s.sub && <div className="devtools-stat-sub mt-1">{s.sub}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {stats.total === 0 ? (
            <div className="card shadow-sm">
              <div className="card-body text-center text-muted py-5">
                No errors were touched during this week.
              </div>
            </div>
          ) : (
            <>
              {/* ── Per-dev cards ── */}
              {devStats.filter((d) => d.total > 0).length > 0 && (
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <h6 className="text-uppercase text-muted fw-semibold small mb-0">By Developer</h6>
                    <div className="devtools-section-line" />
                  </div>
                  <div className="row g-3">
                    {devStats.filter((d) => d.total > 0).map((dev) => (
                      <div key={dev.id} className="col-md-6 col-xl-4">
                        <div className="card shadow-sm h-100 devtools-dev-card">
                          <div
                            className="card-header py-2 px-3 d-flex align-items-center gap-2"
                            style={{ borderLeft: `4px solid ${dev.color}`, background: "#fafafa" }}
                          >
                            <span
                              className="devtools-avatar flex-shrink-0"
                              style={{ background: dev.color, width: 30, height: 30, fontSize: 11 }}
                            >
                              {devInitials(dev.id)}
                            </span>
                            <span className="fw-semibold">{dev.label}</span>
                            <div className="ms-auto d-flex align-items-center gap-2">
                              <span className="badge bg-secondary rounded-pill">{dev.total}</span>
                              {dev.prevTotal > 0 && (
                                <DeltaChip current={dev.total} prev={dev.prevTotal} lowerIsBetter={false} />
                              )}
                            </div>
                          </div>
                          <div className="px-3 pt-2 pb-1 d-flex gap-3 flex-wrap" style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <span className="devtools-weekly-ministat text-success">✓ {dev.done} done</span>
                            <span className="devtools-weekly-ministat text-primary">◎ {dev.acknowledged} ack</span>
                            {dev.open > 0 && <span className="devtools-weekly-ministat text-danger">● {dev.open} open</span>}
                            {dev.withPr > 0 && <span className="devtools-weekly-ministat text-muted">🔗 {dev.withPr} PR</span>}
                          </div>
                          <div className="card-body p-0">
                            {dev.items.map((e, i) => {
                              const si  = STATUS_LABELS[e.status] || STATUS_LABELS.open;
                              const upd = e.updatedAt?.toDate ? fmtDateShort(e.updatedAt.toDate()) : null;
                              return (
                                <div
                                  key={e.groupId}
                                  className="devtools-weekly-item"
                                  style={{ borderTop: i === 0 ? "none" : "1px solid #f3f3f3" }}
                                >
                                  <div className="d-flex align-items-start gap-2">
                                    <span className={`badge devtools-weekly-status-badge ${si.btnClass} text-white flex-shrink-0 mt-1`}>
                                      {si.label}
                                    </span>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div className="devtools-weekly-errmsg fw-medium small">
                                        <TruncatedText text={e.errorMessage || e.id} limit={100} />
                                      </div>
                                      <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                        {e.service && <span className="devtools-weekly-meta">{e.service}</span>}
                                        {e.count > 0 && <span className="devtools-weekly-meta">{Number(e.count).toLocaleString()} occ</span>}
                                        {upd && <span className="devtools-weekly-meta">· {upd}</span>}
                                        {e.prLink && (
                                          <a href={e.prLink} target="_blank" rel="noopener noreferrer" className="devtools-weekly-link text-success">PR ↗</a>
                                        )}
                                        {e.consoleUrl && (
                                          <a href={e.consoleUrl} target="_blank" rel="noopener noreferrer" className="devtools-weekly-link text-muted">GCP ↗</a>
                                        )}
                                      </div>
                                      {e.notes && <div className="devtools-weekly-notes mt-1">{e.notes}</div>}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Full activity log ── */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <h6 className="text-uppercase text-muted fw-semibold small mb-0">
                    Full activity log
                    <span className="text-muted fw-normal ms-1">({stats.total})</span>
                  </h6>
                  <div className="devtools-section-line" />
                  {unassigned.length > 0 && (
                    <span className="badge bg-warning text-dark">
                      {unassigned.length} unassigned
                    </span>
                  )}
                </div>
                <div className="card shadow-sm">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0 devtools-weekly-table">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "2rem" }}>#</th>
                          <th>Error</th>
                          <th style={{ width: "5.5rem" }} className="text-end">Occ</th>
                          <th style={{ width: "7rem" }}>Assignee</th>
                          <th style={{ width: "8rem" }}>Status</th>
                          <th style={{ width: "8.5rem" }}>Updated</th>
                          <th style={{ width: "3.5rem" }}>PR</th>
                          <th style={{ width: "3.5rem" }}>GCP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enriched.map((e, idx) => {
                          const si  = STATUS_LABELS[e.status] || STATUS_LABELS.open;
                          const dev = DEVS.find((d) => d.id === e.assignee);
                          const upd = e.updatedAt?.toDate
                            ? e.updatedAt.toDate().toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—";
                          return (
                            <tr key={e.groupId}>
                              <td className="text-muted align-middle">{idx + 1}</td>
                              <td className="align-middle">
                                <div className="devtools-weekly-errmsg fw-medium">
                                  <TruncatedText text={e.errorMessage || e.id} limit={100} />
                                </div>
                                {e.service && <div className="devtools-weekly-meta mt-1">{e.service}</div>}
                                {e.notes && <div className="devtools-weekly-notes mt-1">{e.notes}</div>}
                              </td>
                              <td className="align-middle text-end">
                                <span className={`small fw-semibold ${getCountClass(Number(e.count) || 0)}`}>
                                  {e.count ? Number(e.count).toLocaleString() : "—"}
                                </span>
                              </td>
                              <td className="align-middle">
                                {dev ? (
                                  <div className="d-flex align-items-center gap-1">
                                    <span
                                      className="devtools-avatar flex-shrink-0"
                                      style={{ background: dev.color, width: 20, height: 20, fontSize: 8 }}
                                    >
                                      {devInitials(dev.id)}
                                    </span>
                                    <span className="small">{dev.label}</span>
                                  </div>
                                ) : (
                                  <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }}>unassigned</span>
                                )}
                              </td>
                              <td className="align-middle">
                                <span className={`badge ${si.btnClass} text-white`}>{si.label}</span>
                              </td>
                              <td className="text-muted small align-middle text-nowrap">{upd}</td>
                              <td className="align-middle">
                                {e.prLink
                                  ? <a href={e.prLink} target="_blank" rel="noopener noreferrer" className="text-success small">PR ↗</a>
                                  : <span className="text-muted">—</span>}
                              </td>
                              <td className="align-middle">
                                {e.consoleUrl
                                  ? <a href={e.consoleUrl} target="_blank" rel="noopener noreferrer" className="text-muted small">GCP ↗</a>
                                  : <span className="text-muted">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

// ─── Stale Tasks Tab ──────────────────────────────────────────────────────────

const STALE_ALERT_URL =
  "https://console.cloud.google.com/monitoring/alerting/alerts/0.o66wwxb6mn36?channelType=slack&project=hey-addy-chatgpt";

const HOURS_OPTIONS = [
  { value: 1,   label: "1h" },
  { value: 6,   label: "6h" },
  { value: 24,  label: "24h" },
  { value: 72,  label: "3d" },
  { value: 168, label: "7d" },
];

function StaleTasksTab() {
  const [staleTasks, setStaleTasks]       = useState([]);
  const [loading, setLoading]             = useState(false);
  const [fetchError, setFetchError]       = useState(null);
  const [hours, setHours]                 = useState(24);
  const [scannedEntries, setScannedEntries] = useState(null);
  const [rerunning, setRerunning]         = useState(new Set()); // set of loanIds currently rerunning
  const [rerunResults, setRerunResults]   = useState({}); // loanId → "success" | "error"

  const fetchStaleTasks = useCallback(async (h = hours) => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/stale-tasks?hours=${h}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.reason || `HTTP ${res.status}`);
      setStaleTasks(data.staleTasks || []);
      setScannedEntries(data.scannedEntries ?? null);
    } catch (err) {
      setFetchError(err.message);
      toast.error(`Failed to fetch stale tasks: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchStaleTasks(hours);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHoursChange = (h) => {
    setHours(h);
    fetchStaleTasks(h);
  };

  const handleExportCsv = () => {
    if (staleTasks.length === 0) return;

    const formatTs = (ts) => (ts ? new Date(ts).toISOString() : "");

    const header = ["Organization ID", "Loan ID", "Stale Since", "Last Seen In Logs", "Occurrences", "Loan URL"];
    const rows = staleTasks.map((t) => [
      t.organizationId,
      t.loanId,
      formatTs(t.updatedAt),
      formatTs(t.lastSeenAt),
      t.occurrences,
      `https://app.addy.so/loans?tab=board&loan=${t.loanId}&tabValue=processingChecklist`,
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stale-tasks-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRerun = async (organizationId, loanId) => {
    setRerunning((prev) => new Set([...prev, loanId]));
    setRerunResults((prev) => { const n = { ...prev }; delete n[loanId]; return n; });

    try {
      const res = await fetch(
        `${mainApiUrl}/organization/${organizationId}/loan-processing-checklist`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ loanId, forceRun: true }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setRerunResults((prev) => ({ ...prev, [loanId]: "success" }));
      toast.success(`Checklist rerun triggered for loan ${loanId}`);
    } catch (err) {
      setRerunResults((prev) => ({ ...prev, [loanId]: "error" }));
      toast.error(`Rerun failed for ${loanId}: ${err.message}`);
    } finally {
      setRerunning((prev) => { const s = new Set(prev); s.delete(loanId); return s; });
    }
  };

  const formatTs = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div>
      {/* Header banner */}
      <div className="alert alert-warning d-flex align-items-center gap-3 mb-4" role="alert">
        <span className="fw-semibold">⚠ Stale Processing Checklist Alert</span>
        <a
          href={STALE_ALERT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-warning ms-auto"
        >
          View GCP Alert ↗
        </a>
      </div>

      {/* Controls */}
      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small fw-semibold">Look back:</span>
          {HOURS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`btn btn-sm ${hours === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleHoursChange(opt.value)}
              disabled={loading}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="d-flex gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={handleExportCsv}
            disabled={loading || staleTasks.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchStaleTasks(hours)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Stats */}
      {scannedEntries !== null && !loading && (
        <p className="text-muted small mb-3">
          Scanned <strong>{scannedEntries}</strong> log entries · found{" "}
          <strong>{staleTasks.length}</strong> unique stale loan{staleTasks.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Error */}
      {fetchError && (
        <div className="alert alert-danger mb-3">{fetchError}</div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Fetching stale task logs…
        </div>
      )}

      {/* Empty state */}
      {!loading && !fetchError && staleTasks.length === 0 && (
        <div className="text-center py-5 text-muted">
          <p className="mb-1 fw-semibold">No stale tasks found</p>
          <p className="small">No [PROCESSING_CHECKLIST_STALE_FOR_LOAN] entries in the last {hours}h</p>
        </div>
      )}

      {/* Table */}
      {!loading && staleTasks.length > 0 && (
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="small text-uppercase text-muted fw-semibold">Organization ID</th>
                <th className="small text-uppercase text-muted fw-semibold">Loan ID</th>
                <th className="small text-uppercase text-muted fw-semibold">Stale Since</th>
                <th className="small text-uppercase text-muted fw-semibold">Last Seen In Logs</th>
                <th className="small text-uppercase text-muted fw-semibold text-center">Occurrences</th>
                <th className="small text-uppercase text-muted fw-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staleTasks.map((task) => {
                const loanUrl = `https://app.addy.so/loans?tab=board&loan=${task.loanId}&tabValue=processingChecklist`;
                const isRerunning = rerunning.has(task.loanId);
                const rerunResult = rerunResults[task.loanId];

                return (
                  <tr key={`${task.organizationId}::${task.loanId}`}>
                    <td>
                      <code className="small">{task.organizationId}</code>
                    </td>
                    <td>
                      <code className="small">{task.loanId}</code>
                    </td>
                    <td className="small text-muted">{formatTs(task.updatedAt)}</td>
                    <td className="small text-muted">{formatTs(task.lastSeenAt)}</td>
                    <td className="text-center">
                      <span className={`badge ${task.occurrences >= 10 ? "bg-danger" : task.occurrences >= 3 ? "bg-warning text-dark" : "bg-secondary"}`}>
                        {task.occurrences}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex gap-2 justify-content-center">
                        <a
                          href={loanUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Go to Loan ↗
                        </a>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            rerunResult === "success"
                              ? "btn-success"
                              : rerunResult === "error"
                              ? "btn-danger"
                              : "btn-outline-warning"
                          }`}
                          onClick={() => handleRerun(task.organizationId, task.loanId)}
                          disabled={isRerunning}
                        >
                          {isRerunning
                            ? "Running…"
                            : rerunResult === "success"
                            ? "✓ Rerun sent"
                            : rerunResult === "error"
                            ? "✗ Failed"
                            : "Rerun Checklist"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FailedDocumentsTab() {
  const [documentFailures, setDocumentFailures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [hours, setHours] = useState(24);
  const [scannedEntries, setScannedEntries] = useState(null);

  const fetchDocumentFailures = useCallback(async (h = hours) => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/document-processing-failures?hours=${h}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) throw new Error(data.reason || `HTTP ${res.status}`);
      setDocumentFailures(data.documentFailures || []);
      setScannedEntries(data.scannedEntries ?? null);
      setHours(data.hours ?? h);
    } catch (err) {
      setFetchError(err.message);
      toast.error(`Failed to fetch document failures: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchDocumentFailures(hours);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHoursChange = (h) => {
    setHours(h);
    fetchDocumentFailures(h);
  };

  const formatTs = (ts) => {
    if (!ts) return "—";
    return new Date(ts).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const formatCsvTs = (ts) => (ts ? new Date(ts).toISOString() : "");

  const formatRetryable = (isRetryable) => {
    if (isRetryable === true) return "Retryable";
    if (isRetryable === false) return "Non-retryable";
    return "Retryability unknown";
  };

  const handleExportCsv = () => {
    if (documentFailures.length === 0) return;

    const header = [
      "Organization ID",
      "Loan ID",
      "Document ID",
      "Not Extracted Reason",
      "Retryable",
      "Error Message",
      "Last Seen In Logs",
      "Occurrences",
      "Loan URL",
    ];
    const rows = documentFailures.map((failure) => [
      failure.organizationId || "",
      failure.loanId || "",
      failure.documentId || "",
      failure.notExtractedReason || "",
      failure.isRetryable ?? "",
      failure.errorMessage || "",
      formatCsvTs(failure.lastSeenAt),
      failure.occurrences ?? 0,
      failure.loanLink || "",
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `failed-documents-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="alert alert-secondary d-flex align-items-center gap-3 mb-4" role="alert">
        <span className="fw-semibold">Document processing failures</span>
        <span className="small text-muted">
          Monitoring recently logged non-retryable document processing failures.
        </span>
      </div>

      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small fw-semibold">Look back:</span>
          {HOURS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`btn btn-sm ${hours === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => handleHoursChange(opt.value)}
              disabled={loading}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="d-flex gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={handleExportCsv}
            disabled={loading || documentFailures.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchDocumentFailures(hours)}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {scannedEntries !== null && !loading && (
        <p className="text-muted small mb-3">
          Scanned <strong>{scannedEntries}</strong> log entries · found{" "}
          <strong>{documentFailures.length}</strong> unique failed document{documentFailures.length !== 1 ? "s" : ""}
        </p>
      )}

      {fetchError && (
        <div className="alert alert-danger mb-3">{fetchError}</div>
      )}

      {loading && (
        <div className="text-center py-5 text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Fetching document processing failure logs…
        </div>
      )}

      {!loading && !fetchError && documentFailures.length === 0 && (
        <div className="text-center py-5 text-muted">
          <p className="mb-1 fw-semibold">No failed documents found</p>
          <p className="small">No non-retryable document processing failures were logged in the last {hours}h</p>
        </div>
      )}

      {!loading && documentFailures.length > 0 && (
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="small text-uppercase text-muted fw-semibold">Organization ID</th>
                <th className="small text-uppercase text-muted fw-semibold">Loan ID</th>
                <th className="small text-uppercase text-muted fw-semibold">Document ID</th>
                <th className="small text-uppercase text-muted fw-semibold">Failure Details</th>
                <th className="small text-uppercase text-muted fw-semibold">Last Seen In Logs</th>
                <th className="small text-uppercase text-muted fw-semibold text-center">Occurrences</th>
                <th className="small text-uppercase text-muted fw-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documentFailures.map((failure) => {
                const rowKey = [
                  failure.organizationId || "unknown-org",
                  failure.loanId || "unknown-loan",
                  failure.documentId || "unknown-document",
                ].join("::");
                const occurrenceClass = failure.occurrences >= 10
                  ? "bg-danger"
                  : failure.occurrences >= 3
                    ? "bg-warning text-dark"
                    : "bg-secondary";

                return (
                  <tr key={rowKey}>
                    <td>
                      <code className="small">{failure.organizationId || "—"}</code>
                    </td>
                    <td>
                      <code className="small">{failure.loanId || "—"}</code>
                    </td>
                    <td>
                      <code className="small">{failure.documentId || "—"}</code>
                    </td>
                    <td className="small">
                      <div className="fw-semibold text-body">
                        {failure.notExtractedReason || "Unknown failure"}
                      </div>
                      <div className="text-muted">
                        {formatRetryable(failure.isRetryable)}
                      </div>
                      {failure.errorMessage && (
                        <div className="text-muted mt-1" title={failure.errorMessage}>
                          {failure.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="small text-muted">{formatTs(failure.lastSeenAt)}</td>
                    <td className="text-center">
                      <span className={`badge ${occurrenceClass}`}>
                        {failure.occurrences ?? 0}
                      </span>
                    </td>
                    <td className="text-center">
                      {failure.loanLink ? (
                        <a
                          href={failure.loanLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Go to Loan ↗
                        </a>
                      ) : (
                        <span className="small text-muted">No link</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
